import { FC, useMemo } from "react";
import { Container, Row, Col, Badge } from "react-bootstrap";
import { fromZonedTime } from "date-fns-tz";

import "../custom.css";
import ButtonWrapper from "./ButtonWrapper";
import {
  useProfile,
  useReservations,
  useSites,
  useVehicles,
} from "../api/hooks";
import { useNavigate, useParams } from "react-router-dom";
import ReservationDetails from "./ReservationDetails";
import { AddToCalendarButton } from "add-to-calendar-button-react";

// Temporary interface based on DTO structure until API client is fixed
interface ReservationType {
  id: string;
  uid?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  siteId?: string;
  siteName?: string;
  siteAddress?: string;
  siteTimezone?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  visitorName?: string;
  visitorPhone?: string;
  city?: string;
  status?: string;
  error?: string;
  price?: number;
  type?: string;
}

const Reservations = () => {
  return (
    <Container className="px-5">
      <Row className="gx-2 my-5">
        <Col lg>
          <YourReservations />
        </Col>
      </Row>
    </Container>
  );
};

const YourReservations = () => {
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile();
  const {
    data: reservationsData,
    isLoading: reservationsLoading,
    error: reservationsError,
  } = useReservations();
  const {
    data: sitesData,
    isLoading: sitesLoading,
    error: sitesError,
  } = useSites();
  const {
    data: vehiclesData,
    isLoading: vehiclesLoading,
    error: vehiclesError,
  } = useVehicles();

  // Cast to expected types until API client is fixed
  const reservations = reservationsData as ReservationType[] | undefined;
  const sites = sitesData as any[] | undefined;

  let { id } = useParams();
  const navigate = useNavigate();

  const reservation =
    reservations?.find((r: ReservationType) => r.id === id) || null;

  const onReservationClicked = (reservation: ReservationType) => {
    navigate(`/reservations/${reservation.id}`);
  };

  const showNewEditForm = () => {
    navigate(`/reservations/00000000-0000-0000-0000-000000000000`);
  };

  // Sort reservations: Active/future first (soonest first), then historical (most recent first)
  const sortedReservations = useMemo(() => {
    if (!reservations) {
      return { active: [], historical: [] };
    }

    const now = new Date();
    const active: ReservationType[] = [];
    const historical: ReservationType[] = [];

    reservations.forEach((reservation: ReservationType) => {
      const isCancelled = reservation.status?.toLowerCase() === 'cancelled';
      
      // Calculate end datetime in the site's timezone
      let isPast = false;
      if (reservation.endDate && reservation.endTime && reservation.siteTimezone) {
        // Combine date and time, then convert from site timezone to UTC for comparison
        const endDateTimeInSiteTimezone = `${reservation.endDate}T${reservation.endTime}`;
        const endDateTimeUtc = fromZonedTime(endDateTimeInSiteTimezone, reservation.siteTimezone);
        isPast = endDateTimeUtc < now;
      } else if (reservation.endDate) {
        // Fallback if no time or timezone - just use date
        isPast = new Date(reservation.endDate) < now;
      }
      
      // Put cancelled or past reservations in historical
      if (isCancelled || isPast) {
        historical.push(reservation);
      } else {
        active.push(reservation);
      }
    });

    // Sort active/future: soonest first
    active.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateA - dateB;
    });

    // Sort historical: most recent first
    historical.sort((a, b) => {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
      const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
      return dateB - dateA;
    });

    return { active, historical };
  }, [reservations]);

  // Get sites with permanent access
  const permanentSites = useMemo(() => {
    if (!sites) return [];
    return sites.filter(site => site.accessTypes?.includes("permanent"));
  }, [sites]);

  if (profileLoading || reservationsLoading || sitesLoading) {
    return <div>Loading...</div>;
  }

  if (profileError || reservationsError || sitesError) {
    return (
      <div>
        Error loading data:{" "}
        {profileError?.message ||
          reservationsError?.message ||
          sitesError?.message}
      </div>
    );
  }

  if (!profile || !reservations) {
    return <div>No data available</div>;
  }

  return (
    <>
      <Row className="mb-4">
        <Col>
          <h2>Your Reservations</h2>
        </Col>
        <Col className="text-end">
          {profile.stripeMetadata !== null ? (
            <div style={{ width: "110px", marginLeft: "auto" }}>
              <ButtonWrapper
                buttonText="+ Add"
                fullWidth={true}
                onClick={() => showNewEditForm()}
              />
            </div>
          ) : null}
        </Col>
      </Row>
      
      {profile.stripeMetadata === null && (
        <Row>
          <Col>
            <div className="alert alert-warning mb-4">
              Please add a payment method on the{" "}
              <a href="/details/payment" className="alert-link">
                Details
              </a>{" "}
              tab before making a reservation.
            </div>
          </Col>
        </Row>
      )}

      {/* Permanent Parker Locations */}
      {permanentSites.length > 0 && (
        <Row className="mb-4">
          <Col>
            <div style={{
              backgroundColor: "#fff7ed",
              border: "1px solid #fdba74",
              borderRadius: "8px",
              padding: "16px"
            }}>
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                marginBottom: "12px",
                gap: "8px"
              }}>
                <h5 style={{ margin: 0, color: "#c2410c", fontWeight: 600 }}>
                  Your Permanent Parker Locations
                </h5>
              </div>
              <div style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "12px" 
              }}>
                {permanentSites.map((site: any) => (
                  <div 
                    key={site.id}
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "16px",
                      minWidth: "280px",
                      flex: "1 1 280px",
                      maxWidth: "400px"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: "#111827", marginBottom: "2px" }}>
                        {site.name}
                      </div>
                      {site.address && (
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {site.address}
                        </div>
                      )}
                    </div>
                    {profile.stripeMetadata !== null && (
                      <button
                        onClick={() => navigate(`/reservations/00000000-0000-0000-0000-000000000000?siteId=${site.id}`)}
                        style={{
                          backgroundColor: "#f97316",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 12px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                          whiteSpace: "nowrap"
                        }}
                      >
                        + Reserve
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Active & Future Reservations */}
      {sortedReservations.active.length > 0 && (
        <>
          <Row className="mb-2">
            <Col>
              <h5 className="text-dark">
                Active & Upcoming{" "}
                <Badge bg="secondary" className="ms-1">{sortedReservations.active.length}</Badge>
              </h5>
            </Col>
          </Row>
          <Row>
            <Col>
              {sortedReservations.active.map((reservation: ReservationType) => (
                <Reservation
                  reservation={reservation}
                  key={reservation.id}
                  onReservationClicked={() => onReservationClicked(reservation)}
                  isHistorical={false}
                />
              ))}
            </Col>
          </Row>
        </>
      )}

      {/* Historical Reservations */}
      {sortedReservations.historical.length > 0 && (
        <>
          <Row className="mt-4 mb-2">
            <Col>
              <h5 className="text-dark">
                Past{" "}
                <Badge bg="secondary" className="ms-1">{sortedReservations.historical.length}</Badge>
              </h5>
            </Col>
          </Row>
          <Row>
            <Col>
              {sortedReservations.historical.map((reservation: ReservationType) => (
                <Reservation
                  reservation={reservation}
                  key={reservation.id}
                  onReservationClicked={() => onReservationClicked(reservation)}
                  isHistorical={true}
                />
              ))}
            </Col>
          </Row>
        </>
      )}

      {reservations && reservations.length === 0 && (
        <Row>
          <Col>
            <div className="text-muted py-3">
              <p>You have no reservations</p>
            </div>
          </Col>
        </Row>
      )}
      {id && (
        <ReservationDetails
          reservation={reservation as ReservationType}
          profile={profile}
          vehicles={vehiclesData || []}
          sites={sites || []}
        />
      )}
    </>
  );
};

const Reservation: FC<{
  reservation: ReservationType;
  onReservationClicked: () => void;
  isHistorical: boolean;
}> = ({ reservation, onReservationClicked, isHistorical }) => {
  const startDate = reservation.startDate
    ? new Date(reservation.startDate)
    : new Date();
  const endDate = reservation.endDate
    ? new Date(reservation.endDate)
    : new Date();

  const isCancelled = reservation.status?.toLowerCase() === "cancelled";

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = () => {
    const status = reservation.status?.toLowerCase() || "";
    let badgeStyle: React.CSSProperties = {
      backgroundColor: 'rgba(108, 117, 125, 0.1)',
      color: '#6c757d',
      border: '1px solid rgba(108, 117, 125, 0.2)',
      fontWeight: 500
    };
    
    if (status === "active") {
      badgeStyle = {
        backgroundColor: 'rgba(25, 135, 84, 0.1)',
        color: '#198754',
        border: '1px solid rgba(25, 135, 84, 0.2)',
        fontWeight: 500
      };
    } else if (status === "confirmed") {
      badgeStyle = {
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        color: '#0d6efd',
        border: '1px solid rgba(13, 110, 253, 0.2)',
        fontWeight: 500
      };
    } else if (status === "cancelled") {
      badgeStyle = {
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        color: '#dc3545',
        border: '1px solid rgba(220, 53, 69, 0.2)',
        fontWeight: 500
      };
    } else if (status === "completed") {
      badgeStyle = {
        backgroundColor: 'rgba(108, 117, 125, 0.1)',
        color: '#6c757d',
        border: '1px solid rgba(108, 117, 125, 0.2)',
        fontWeight: 500
      };
    }

    return (
      <Badge bg="" style={badgeStyle}>
        {reservation.status || "Unknown"}
      </Badge>
    );
  };

  return (
    <div 
      className={`card mb-3 ${isHistorical ? 'opacity-75' : ''}`}
      style={{ 
        cursor: 'pointer',
        ...(isCancelled ? {
          backgroundColor: '#fef2f2',
          borderColor: '#fecaca',
          opacity: 0.8
        } : {})
      }}
      onClick={() => onReservationClicked()}
    >
      <div className="card-body p-3">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <h6 
            className="card-title mb-0 fw-semibold"
            style={isCancelled ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}
          >
            {reservation.siteName}
          </h6>
          <div className="d-flex gap-2">
            {reservation.type?.toUpperCase() === "STRATA" && (
              <Badge bg="" style={{
                backgroundColor: 'rgba(111, 66, 193, 0.1)',
                color: '#6f42c1',
                border: '1px solid rgba(111, 66, 193, 0.2)',
                fontWeight: 500
              }}>
                Strata Visitor
              </Badge>
            )}
            {getStatusBadge()}
          </div>
        </div>
        
        <p className="text-muted small mb-3">{reservation.siteAddress}</p>

        {/* Visitor details for strata bookings */}
        {reservation.type?.toUpperCase() === "STRATA" && (
          <div className="bg-light rounded p-2 mb-3">
            <div className="row g-2">
              <div className="col-3">
                <div className="text-muted small">Visitor</div>
                <div className="small fw-medium">{reservation.visitorName || "-"}</div>
              </div>
              <div className="col-3">
                <div className="text-muted small">Plate</div>
                <div className="small fw-medium">{reservation.vehiclePlate || "-"}</div>
              </div>
              <div className="col-3">
                <div className="text-muted small">Phone</div>
                <div className="small fw-medium">{reservation.visitorPhone || "-"}</div>
              </div>
              <div className="col-3">
                <div className="text-muted small">QR Code</div>
                <a
                  href={`/visitor/${reservation.uid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small fw-medium text-primary text-decoration-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  📱 View
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="row g-3 mb-3">
          <div className="col-6">
            <div className="text-muted small mb-1">Date</div>
            <div className="small">{formatDate(startDate)}</div>
          </div>
          <div className="col-6">
            <div className="text-muted small mb-1">Time</div>
            <div className="small">
              {getTimeString(reservation.startTime)} - {getTimeString(reservation.endTime)}
            </div>
          </div>
        </div>

        <hr className="my-2" />

        <div className="d-flex align-items-center justify-content-between">
          <span className="fw-semibold">Total Cost</span>
          <span className="fs-5 fw-bold">
            {reservation.price ? `$${reservation.price.toFixed(2)}` : "-"}
          </span>
        </div>

        {!isHistorical && (
          <>
            <hr className="my-2" />
            <div className="d-flex justify-content-center">
              <AddToCalendarButton
                name={"Parker reservation @ " + reservation.siteName}
                options={[
                  "Apple",
                  "Google",
                  "Yahoo",
                  "Microsoft365",
                  "Outlook.com",
                  "iCal",
                ]}
                startDate={reservation.startDate}
                endDate={reservation.endDate}
                startTime={reservation.startTime}
                endTime={reservation.endTime}
                timeZone={reservation.siteTimezone}
                description="Parking Reservation"
                location={reservation.siteAddress}
              />
            </div>
          </>
        )}

        {reservation.error && (
          <div className="alert alert-danger mb-0 mt-2 py-2">{reservation.error}</div>
        )}
      </div>
    </div>
  );
};

const getTimeString = (time: string | undefined) => {
  if (!time) return;

  if (time < "12:00") {
    return time + " AM";
  }

  if (time < "13:00") {
    return time + " PM";
  }

  const timeFragments = time.split(":");
  const newTime =
    Number(timeFragments[0]) - 12 + ":" + timeFragments[1] + " PM";
  return newTime;
};

export default Reservations;
